import { MbeInput, PasswordFeedback, PasswordInput, TermsSignupMessage } from '@/components/shared';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Flex,
  Heading,
  Link,
  useControllableState,
  useDisclosure,
} from '@chakra-ui/react';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import type { IBaseForm, IUser } from '@/types';
import {
  emailRegex,
  ERRORS,
  hasDigits,
  hasSpecialChar,
  hasUpperLetter,
  minLength,
  phoneRegex,
  ROUTE_PATHS,
} from '@/constants';
import { hasEmptyFields } from '@/utils';
import { AddressFields } from '@/components/shared/AddressFields';
import { useUserApi, useValidator } from '@/features/users';

export const PersonSignUpForm: React.FC<IBaseForm<IUser>> = ({
  defaultValues,
  onSubmit: providedOnSubmit,
}): JSX.Element => {
  const { i18n, t } = useTranslation();
  const { validateFiscalCode } = useValidator();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const userApi = useUserApi();
  const [isMailUsed, setIsMailUsed] = useControllableState({ defaultValue: false });

  const methods = useForm<IUser>({
    defaultValues,
    mode: 'onBlur',
    criteriaMode: 'all',
  });

  const {
    formState: { errors },
    getValues,
    handleSubmit: submitHandler,
    register,
    watch,
  } = methods;

  const formFields = watch([
    'firstName',
    'lastName',
    'city',
    'countryCode',
    'email',
    'phone',
    'streetAddress',
    'countryCode',
    'province',
  ]);

  const areRequiredFieldsEmpty = hasEmptyFields(formFields);
  const isDisabled = !!errors.password?.types || areRequiredFieldsEmpty || isMailUsed;

  const handleSubmit: SubmitHandler<IUser> = (data, event): void => {
    event?.preventDefault();
    const updatedUserData = { ...data, customerType: 'PERSON', locale: i18n.language };

    providedOnSubmit?.(updatedUserData);
  };

  const countryCodeValue = getValues('countryCode') && getValues('countryCode').toLowerCase();

  const fiscalCodeProps = register('fiscalCode', {
    validate: (value) => validateFiscalCode({ countryCode: countryCodeValue, fiscalCode: value }),
  });

  return (
    <FormProvider {...methods}>
      <form
        autoComplete="off"
        id="signUpForm"
        name="signUpForm"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={submitHandler(handleSubmit)}
      >
        <Flex direction="column" gap={12}>
          <MbeInput
            error={errors.firstName}
            id="firstName"
            placeholder={t('FORM.FIRSTNAME')}
            {...register('firstName', {
              required: { value: true, message: t(ERRORS.empty) },
            })}
          />
          <MbeInput
            error={errors.lastName}
            id="lastName"
            placeholder={t('FORM.LASTNAME')}
            {...register('lastName', {
              required: { value: true, message: t(ERRORS.empty) },
            })}
          />
          <MbeInput
            error={errors.fiscalCode}
            id="fiscalCode"
            placeholder={t('FORM.FISCALCODE')}
            {...fiscalCodeProps}
          />
          <MbeInput
            error={errors.email}
            id="email"
            placeholder={t('FORM.EMAIL')}
            {...register('email', {
              required: { value: true, message: t(ERRORS.empty) },
              pattern: { value: emailRegex, message: t(ERRORS.email) },
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onBlur: async (): Promise<void> => {
                const isMailKnown: boolean = await userApi.checkUserEmail(getValues('email'));
                setIsMailUsed(isMailKnown);
              },
            })}
          />
          {isMailUsed && (
            <Alert my={-8} status="error" w="100%">
              <AlertIcon />
              <AlertDescription>
                <Trans
                  components={{
                    url: <Link color="brand.red.light" href={ROUTE_PATHS.userLogin} />,
                  }}
                  i18nKey="ERRORS.ALREADY_USED_EMAIL"
                />
              </AlertDescription>
            </Alert>
          )}
          <MbeInput
            error={errors.phone}
            id="phone"
            placeholder={t('FORM.PHONE')}
            {...register('phone', {
              required: { value: true, message: t(ERRORS.empty) },
              pattern: { value: phoneRegex, message: t(ERRORS.phoneNumber) },
            })}
          />
          <Heading as="h2" mb={-8} size="md">
            {t('SIGNUP.FORM.ADDRESS_FIELD_TITLE')}
          </Heading>
          <AddressFields />
          <Heading as="h2" mb={-8} size="md">
            {t('SIGNUP.FORM.PASSWORD_FIELD_TITLE')}
          </Heading>
          <PasswordFeedback
            error={errors.password}
            isOpen={isOpen}
            value={getValues('password')}
            onClose={onClose}
          >
            <PasswordInput
              error={errors.password}
              id="password"
              placeholder={t('FORM.PASSWORD')}
              {...register('password', {
                required: true,
                validate: {
                  minLength: (value) => minLength(value, 8),
                  hasUpperLetter,
                  hasSpecialChar,
                  hasDigits,
                },
                onBlur: onClose,
                onChange: () => {
                  if (!isOpen) onOpen();
                },
              })}
            />
          </PasswordFeedback>
          <TermsSignupMessage />
        </Flex>
        <Button disabled={isDisabled} mt={8} type="submit" variant="mbeSecondary" w="full">
          {t('ACTIONS.CONFIRM')}
        </Button>
      </form>
    </FormProvider>
  );
};
